import { Injectable } from '@angular/core';
import { IdPResponseError } from '@enums/idpresponseerrors.enum';
import { StorageKeys } from '@enums/storage.enum';
import { environment } from '@environments/environment';
import { CompanyDataSaved } from '@models/entities/copny/copny';
import { CodeVerify, Subscription, User } from '@models/entities/user';
import { IdpAccessTokenResponse, IdpIdToken, IdpMeResponse, UserInfoDesResponse } from '@models/responses/login-response';
import { ResponseGeneral, ResponseGeneralMini } from '@models/responses/response-general';
import { lastValueFrom, Observable } from 'rxjs';
import { CopnyManagmentService } from '@providers/copny-managment/copny-managment.service';
import { SessionDataProvider } from '@providers/session-data/session-data';
import { SnackbarService } from '@providers/snackbar-services/snackbar.services';
import { StorageService } from '@providers/storage/storage.service';
import { TranslationService } from '@providers/translation-new/translation.service';
import { UserManagerService } from '@providers/user-manager/user-manager.service';
import jwt_decode from 'jwt-decode';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Options } from '@models/entities/general/general';
import { MethodsGeneralService } from '@providers/methods-general/methods-general/methods-general';
import { IdPParamKeys } from '@enums/idpparamkeys.enum';
import { ModalService } from '@providers/modal-services/modal.service';
import { LoaderManagmentService } from '@providers/loader-managment/loader-managment.service';
import { Router } from '@angular/router';
import { NormalModalComponent } from '@components/normal-modal/normal-modal';
import { ROUTES_ADMIN } from '@constants/routes';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  passerIdP: {
    options: Options;
  };

  private _url: string;
  private _unauthorized = false;
  private readonly token = '/token';
  private readonly userSubscriptionsInfo =
    '/api/providers/Passer.Management/accounts/{{account}}/subscriptions';
  private readonly userInfo = '/me';
  private readonly getUserInfo = '/system/getUser';
  private readonly resetPassword = '/api/providers/Passer.Authorization/recovery/password';
  private readonly sendCode = '/oauth/user/otp';
  private readonly verifyTwoFactor = '/oauth//user/verifyOtp';
  private readonly logOut =
    '/api/providers/Passer.Authorization/accounts/{{userSub}}/logout';

  constructor(
    private copnyManagment: CopnyManagmentService,
    private http: HttpClient,
    private loading: LoaderManagmentService,
    private methodGeneral: MethodsGeneralService,
    private modalService: ModalService,
    private router: Router,
    private session: SessionDataProvider,
    private snackbar: SnackbarService,
    private storage: StorageService,
    private translation: TranslationService,
    private userManagerService: UserManagerService
  ) {
    this.passerIdP = { options: environment.passerIdP.options };
  }

  private get url(): string {
    this._url = environment.serverUrl;
    return this._url;
  }

  async loginStart(
    email: string,
    password: string
  ): Promise<IdpAccessTokenResponse> {
    const requestHeaders = new HttpHeaders().append(
      'Content-type',
      'application/x-www-form-urlencoded'
    );
    const accessTokenRequest = {
      grant_type: 'password',
      client_id: this.passerIdP.options.clientId,
      client_secret: this.passerIdP.options.clientsecret,
      username: email,
      password: password,
      scope: this.passerIdP.options.scope,
    };

    const login$ = this.http.post<IdpAccessTokenResponse>(
      this.passerIdP.options.serverUrl + this.token,
      this.methodGeneral.toQueryString(accessTokenRequest),
      { headers: requestHeaders }
    );

    return await lastValueFrom(login$);
  }

  async userSubscriptionsInfoMethod(sub: string): Promise<Subscription[]> {
    const requestHeaders = new HttpHeaders({
      'Content-type': 'application/x-www-form-urlencoded',
      'Authorization': `Bearer ${this.session.token}`
    });

    const subscriptions$ = this.http.get<Subscription[]>(
      this.passerIdP.options.serverUrl +
        this.userSubscriptionsInfo.replace(IdPParamKeys.Account, sub),
      { headers: requestHeaders }
    );

    return await lastValueFrom(subscriptions$);
  }

  async refresh(
    refresh_token: string,
    managementGroup: string,
    suscription: string
  ): Promise<IdpAccessTokenResponse> {
    const basicAuthBase64 = btoa(
      `${this.passerIdP.options.clientId}:${this.passerIdP.options.clientsecret}`
    );
    const requestHeaders = new HttpHeaders({
      'Content-type': 'application/x-www-form-urlencoded',
      Authorization: `Basic ${basicAuthBase64}`,
    });

    const accessTokenRequest = {
      grant_type: 'refresh_token',
      refresh_token,
      scope: this.passerIdP.options.scope,
      suscription,
      managementGroup,
    };

    const refresh$ = this.http.post<IdpAccessTokenResponse>(
      this.passerIdP.options.serverUrl + this.token,
      this.methodGeneral.toQueryString(accessTokenRequest),
      { headers: requestHeaders }
    );

    return await lastValueFrom(refresh$);
  }

  async userInfoMethod(): Promise<IdpMeResponse> {
    const requestHeaders = new HttpHeaders().append(
      'Content-type',
      'application/x-www-form-urlencoded'
    );

    const user$ = this.http.get<IdpMeResponse>(
      this.passerIdP.options.serverUrl + this.userInfo,
      { headers: requestHeaders }
    );

    return await lastValueFrom(user$);
  }

  async getUserInfoMethod(): Promise<UserInfoDesResponse> {
    const user$ = this.http.get<UserInfoDesResponse>(
      `${this.url}${this.getUserInfo}?${this.methodGeneral.toQueryString({})}`
    );

    return await lastValueFrom(user$);
  }

  refreshToken(refresh_token: string, managementGroup: string, suscription: string): Observable<IdpAccessTokenResponse> {
    const basicAuthBase64 = btoa(
      `${this.passerIdP.options.clientId}:${this.passerIdP.options.clientsecret}`
    );
    const requestHeaders = new HttpHeaders({
      'Content-type': 'application/x-www-form-urlencoded',
      Authorization: `Basic ${basicAuthBase64}`,
    });

    const accessTokenRequest = {
      grant_type: 'refresh_token',
      refresh_token,
      scope: this.passerIdP.options.scope,
      managementGroup,
      suscription
    };

    return this.http.post<IdpAccessTokenResponse>(
      this.passerIdP.options.serverUrl + this.token,
      this.methodGeneral.toQueryString(accessTokenRequest),
      { headers: requestHeaders }
    );
  }

  async forgotPassword(params: { email: string; callbackBaseUrl: string; }): Promise<ResponseGeneral> {
    const forgot$ = this.http.post<ResponseGeneral>(
      this.passerIdP.options.serverUrl + this.resetPassword,
      params
    );

    return await lastValueFrom(forgot$);
  }

  async sendCodeMethod(params: CodeVerify): Promise<ResponseGeneral> {
    const send$ = this.http.post<ResponseGeneral>(
      this.url + this.sendCode,
      params
    );

    return await lastValueFrom(send$);
  }

  async verfyTwoFactorMethod(params: { email: string; verificationCode: string; }): Promise<ResponseGeneral> {
    const verify$ = this.http.get<ResponseGeneral>(
      `${this.url}${this.verifyTwoFactor}?${this.methodGeneral.toQueryString(params)}`
    );

    return await lastValueFrom(verify$);
  }

  async logout(
    userSub: string,
    params: { subscription: string }
  ): Promise<ResponseGeneral> {
    const logout$ = this.http.post<ResponseGeneral>(
      this.passerIdP.options.serverUrl +
        this.logOut.replace(IdPParamKeys.UserSub, userSub),
      params
    );

    return await lastValueFrom(logout$);
  }

  async logoutMethod(unauthorized = false): Promise<void> {
    try {
      this.loading.presentLoading();
      const { subscriptionIdp } = this.session;
      await this.logout(this.userManagerService?.sub, { subscription: subscriptionIdp?._id });
      await this.clearDataLogout(unauthorized);
    } catch (_) {
      await this.clearDataLogout(unauthorized);
    }
  }

  async clearDataLogout(logout?: boolean): Promise<void> {
    this.session.clearAllData();
    this.session.header = false;
    this.modalService.closeModal();
    await this.storage.clearAllData();
    this.userManagerService.currentUser = undefined;
    environment.serverUrl = environment.serverUrlAdmin;
    setTimeout(() => {
      this.loading.dismissLoading();
      this.router.navigate([ROUTES_ADMIN.ROOT_ROUTE]);

      if (!logout) {
        const { login: { alerts: { closeT, closeD } }, actions: { understood } } = this.translation.getLangMessagesByKeys(['login', 'actions']);
        this.modalService.showAlertComp(NormalModalComponent, {
          title: closeT,
          message: closeD,
          btnSave: understood
        });
      }
    }, 100);
    this._unauthorized = false;
  }

  async initLogin(email: string, password: string): Promise<ResponseGeneralMini> {
    try {
      const response: IdpAccessTokenResponse = await this.loginStart(email, password);
      if (response.token_type && response.access_token) {
        return { success: true, data: response };
      } else {
        return { success: false, data: this.revErrors(response) };
      }
    } catch (error) {
      if (error?.message && error?.title) {
        this.snackbar.showSnackBar(error.title, error.message);
      }
      return { success: false, data: '' };
    }
  }

  async loginSelectSubscription(response: IdpAccessTokenResponse): Promise<ResponseGeneralMini> {
    const idToken: IdpIdToken = jwt_decode(response.id_token);

    this.session.token = response.access_token;
    this.session.refreshTokenIdp = response.refresh_token;
    this.session.idpAccessToken = response;
    try {
      const userSubsResponse: Subscription[] = await this.userSubscriptionsInfoMethod(idToken.sub);
      if (userSubsResponse) {
        if (userSubsResponse.length === 1) {
          this.session.subscriptionIdp = userSubsResponse[0];
          return { success: true, data: userSubsResponse };
        } else {
          return { success: true, data: userSubsResponse };
        }
      } else {
        this.loading.dismissLoading();
        this.revErrors({code: '5'});
        return { success: false, data: null };
      }
    }
    catch (error) {
      this.loading.dismissLoading();
      if (error?.message && error?.title) {
        this.snackbar.showSnackBar(error.title, error.message);
      }
      return { success: false, data: null };
    }

  }

  async logingResfreshToken(userSubscriptionsToclient: Subscription[]): Promise<boolean> {
    try {
      const idpAccessTokenResponse: IdpAccessTokenResponse = await this.refresh(
        this.session.refreshTokenIdp,
        this.session.subscriptionIdp.managementGroup?._id,
        this.session.subscriptionIdp._id,
      );

      this.session.token = idpAccessTokenResponse.access_token;
      this.session.tokenType = idpAccessTokenResponse.token_type;
      this.session.refreshTokenIdp = idpAccessTokenResponse.refresh_token;
      this.session.subscriptionsIdp = userSubscriptionsToclient;
      this.session.idpAccessToken = idpAccessTokenResponse;

      return true;
    } catch (error) {
      this.loading.dismissLoading();
      if (error?.message && error?.title) {
        this.snackbar.showSnackBar(error.title, error.message);
      }
      return false;
    }
  }

  async loginSaveUser(): Promise<ResponseGeneralMini> {
    try {
      const userInfoIdpMeResponse: IdpMeResponse = await this.userInfoMethod();
      this.userManagerService.temporaryIdSub = userInfoIdpMeResponse?.sub;
      const userInfoDesResponse: UserInfoDesResponse = await this.getUserInfoMethod();
      const copnyUser = this.userManagerService.createUserToSave(userInfoIdpMeResponse, userInfoDesResponse);

      if (typeof (Storage) !== 'undefined') {
        return { success: true, data: copnyUser };
      } else {
        this.loading.dismissLoading();
        const { alerts } = this.translation.getLangMessagesByKeys('login');
        this.snackbar.showSnackBar('', alerts.local);
        return { success: false, data: null };
      }
    } catch (error) {
      this.loading.dismissLoading();
      this.userManagerService.temporaryIdSub = '';
      if (error?.message && error?.title) {
        this.snackbar.showSnackBar(error.title, error.message);
      }
      return { success: false, data: null };
    }
  }

  async loginGetInfoCopny(copnyUser: User): Promise<boolean> {
    const result: CompanyDataSaved = await this.copnyManagment.getCopnyss(copnyUser);
    if (result) {
      return true;
    } else {
      this.revErrorIDP('');
      return false;
    }
  }

  async selectedCopny(copnyUser: User): Promise<boolean> {
    const copnyFound: CompanyDataSaved = { ...this.copnyManagment.company };
    if (!!copnyFound) {
      if (copnyFound?.subscription?.fk_company_suscription_status === 2) {
        this.userManagerService.saveUser(copnyUser);
        this.session.isLogged = true;
        await this.setSessionDataStorage();
        return true;
      } else {
        return false;
      }
    }
  }

  async setSessionDataStorage(): Promise<void> {
    await this.storage.setData(StorageKeys.TOKEN, this.session.token);
    await this.storage.setData(StorageKeys.TOKEN_TYPE, this.session.tokenType);
    await this.storage.setData(StorageKeys.REFRESH_TOKEN_IDP, this.session.refreshTokenIdp);
    await this.storage.setData(StorageKeys.SUBSCRIPTION_IDP, this.session.subscriptionIdp);
    await this.storage.setData(StorageKeys.SUBSCRIPTIONS_IDP, this.session.subscriptionsIdp);
    await this.storage.setData(StorageKeys.idpAccessToken, this.session.idpAccessToken);
  }

  revErrors(data: any): string {
    const { alerts, login } = this.translation.getLangMessagesByKeys(['alerts', 'login']);
    if (data && data.code) {
      switch ('' + data.code) {
        case '0':
          return 'alerts.log0';
        case '2':
          return 'alerts.log2';
        case '3':
          return 'alerts.log3';
        case '4':
          return 'alerts.log4';
        case '5':
          this.snackbar.showSnackBar('', alerts.modal.titE);
          return '';
        case '4004':
          this.snackbar.showSnackBar('', alerts.modal.no_susc_add);
          return '';
        case '4005':
          this.snackbar.showSnackBar('', alerts.modal.no_susc_act);
          return '';
        default:
          this.snackbar.showSnackBar('', login.alerts.em_pass);
          return '';
      }
    } else {
      this.snackbar.showSnackBar('', login.alerts.em_pass);
      return '';
    }
  }

  revErrorIDP(error: string): void {
    const { alerts: { passerErrorMessage }, login: { alerts } } = this.translation.getLangMessagesByKeys(['alerts', 'login']);
    switch (error) {
      case IdPResponseError.InvalidGrant:
        this.snackbar.showSnackBar('', alerts.passerIdpErrorInvalidGrant);
        break;
      case IdPResponseError.InvalidManagementGroup:
        this.snackbar.showSnackBar('', alerts.passerIdpErrorInvalidManagementGroup);
        break;
      case IdPResponseError.InvalidSubscription:
        this.snackbar.showSnackBar('', alerts.passerIdpErrorInvalidSubscription);
        break;
      default:
        this.snackbar.showSnackBar('', passerErrorMessage);
        break;
    }
  }

  public get unauthorized(): boolean {
    return this._unauthorized;
  }

  public set unauthorized(value: boolean) {
    this._unauthorized = value;
  }
}
